import type { VehicleSocket } from "../../../store/admin/vehiclesSocketStore";

/**
 * Forma estructural mínima de cada assignment para descubrir el driver.
 *
 * El backend (`tripsController._resolveDriversForVehicleIds`) inyecta el
 * objeto `driver` con `user_id` y `email` resueltos vía `device.vehicle_id →
 * drivers → users`. Lo aceptamos opcional para no romper a callers viejos
 * que pasan el trip "crudo" (assignments sin enriquecer).
 */
export interface TripDriverAssignment {
  device_id?: string;
  status?: string;
  driver?: {
    user_id?: string | null;
    email?: string | null;
  } | null;
}

/**
 * Forma estructural mínima de un trip para extraer drivers.
 *
 * Compatible tanto con el `Trip` del lite-sidebar (store/admin/tripsStore) como
 * con el del admin (Layouts/admin/.../trips/types). No importamos ninguna de
 * las dos para evitar acoplar este helper a un módulo concreto.
 */
export interface TripDriverSource {
  vehicle_id?: string;
  remolque_id?: string;
  assignments?: TripDriverAssignment[];
}

/**
 * Resultado de la inspección de un trip: tanto user_ids como emails de los
 * drivers atados a sus assignments. El consumidor (NewConversationModal)
 * preselecciona participantes preferentemente por `user_id` — match exacto
 * contra el directorio de la company — y usa `email` como fallback cuando
 * solo tenemos el dato del realtime (ahí no exponemos user_id).
 */
export interface TripParticipantSeed {
  userIds: string[];
  emails: string[];
}

/**
 * Junta los participantes-driver de un trip combinando dos fuentes:
 *
 *   1. **Backend enriquecido**: `assignment.driver` poblado por el endpoint
 *      `/trips` o `/tripsList`. Es la fuente preferida porque trae `user_id`
 *      (más confiable que email para preseleccionar usuarios) y no depende
 *      del realtime layer.
 *
 *   2. **Realtime (`vehiclesSocketStore`)**: fallback que cubre dos casos:
 *      (a) trips servidos antes del enriquecimiento en el backend, y
 *      (b) cards de admin/lite que solo tienen el trip "ligero" sin
 *      pasar por la resolución completa. Solo aporta `email`.
 *
 * Devuelve listas únicas (Set) y emails normalizados (lowercase + trim).
 */
export function collectTripParticipantSeed(
  trip: TripDriverSource | null | undefined,
  vehicles: VehicleSocket[],
): TripParticipantSeed {
  const userIds = new Set<string>();
  const emails = new Set<string>();
  if (!trip) return { userIds: [], emails: [] };

  const pushUserId = (id?: string | null) => {
    if (typeof id !== "string") return;
    const trimmed = id.trim();
    if (trimmed) userIds.add(trimmed);
  };
  const pushEmail = (email?: string | null) => {
    if (typeof email !== "string") return;
    const trimmed = email.trim().toLowerCase();
    if (trimmed) emails.add(trimmed);
  };

  const isActiveStatus = (status?: string) =>
    !status || status === "active" || status === "pending";

  // 1) Driver enriquecido por el backend.
  for (const a of trip.assignments || []) {
    if (!a?.device_id) continue;
    if (!isActiveStatus(a.status)) continue;
    pushUserId(a.driver?.user_id);
    pushEmail(a.driver?.email);
  }

  // 2) Fallback realtime (deviceAssignments → vehiclesSocketStore).
  //    El layer realtime solo expone `email` (no `user_id`) en `driver`, así
  //    que aquí solo aporta emails. Si el trip nuevo viene enriquecido, esto
  //    redunda con el paso 1 y el Set se encarga de deduplicar.
  if (Array.isArray(vehicles) && vehicles.length > 0) {
    const pushFromVehicle = (vehicle?: VehicleSocket) => {
      pushEmail(vehicle?.driver?.email);
    };
    if (trip.vehicle_id) {
      pushFromVehicle(vehicles.find((v) => v.vehicle_id === trip.vehicle_id));
    }
    if (trip.remolque_id) {
      pushFromVehicle(vehicles.find((v) => v.remolque_id === trip.remolque_id));
    }
    for (const a of trip.assignments || []) {
      if (!a?.device_id) continue;
      if (!isActiveStatus(a.status)) continue;
      pushFromVehicle(vehicles.find((v) => v.id === a.device_id));
    }
  }

  return { userIds: [...userIds], emails: [...emails] };
}

/**
 * Wrapper "solo-emails" para callers viejos. Internamente delega en
 * `collectTripParticipantSeed`.
 */
export function collectTripDriverEmails(
  trip: TripDriverSource | null | undefined,
  vehicles: VehicleSocket[],
): string[] {
  return collectTripParticipantSeed(trip, vehicles).emails;
}
